import React, { useState } from 'react';
import { FaUser, FaPhone, FaEnvelope, FaStethoscope, FaCalendarAlt, FaClock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import '../style.css';

const AppointmentSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    doctor: '',
    query: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const services = [
    'General Checkup',
    'Cardiology',
    'Neurology',
    'Orthopedics',
    'Pediatrics',
    'Dermatology',
    'ENT',
    'Dental Care',
    'Eye Care',
    'Physiotherapy'
  ];

  const doctors = [
    { 
      name: 'Dr. Sandeep', 
      specialization: 'Cardiologist',
      availability: 'Mon-Fri: 9AM-5PM',
      available: true 
    },
    { 
      name: 'Dr. Shweta Rai', 
      specialization: 'Neurologist',
      availability: 'Tue-Sat: 10AM-6PM',
      available: true 
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Enter valid 10-digit phone';
    
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Enter valid email';
    
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!formData.doctor) newErrors.doctor = 'Please select a doctor';
    if (!formData.query.trim()) newErrors.query = 'Please enter your query';
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length === 0) {
      console.log('Appointment submitted:', formData);
      setIsSubmitted(true);
      
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          doctor: '',
          query: ''
        });
      }, 3000);
    } else {
      setErrors(formErrors);
    }
  };

  const handleDoctorSelect = (doctorName) => {
    handleInputChange({ target: { name: 'doctor', value: doctorName } });
  };

  return (
    <div className="rockland-appointment-section">
      <div className="rockland-appointment-container">
        
        {/* Header Section */}
        <div className="rockland-appointment-header">
          <div className="rockland-header-content">
            <span className="rockland-appointment-badge">Appointments</span>
            <h1 className="rockland-appointment-title">Home – Medical Appointments</h1>
            <h2 className="rockland-appointment-subtitle">GET AN APPOINTMENT</h2>
            <p className="rockland-appointment-description">
              Book your appointment with our expert medical professionals. Fast, reliable, and convenient healthcare scheduling.
            </p>
          </div>
        </div>

        {/* Main Content - Form & Info */}
        <div className="rockland-appointment-content">
          
          {/* Left Side - Appointment Form */}
          <div className="rockland-appointment-form-wrapper">
            <form className="rockland-appointment-form" onSubmit={handleSubmit}>
              
              {/* Name Field */}
              <div className="rockland-form-group">
                <div className="rockland-input-label">
                  <FaUser className="rockland-input-icon" />
                  <label>Name</label>
                </div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`rockland-form-input ${errors.name ? 'rockland-input-error' : ''}`}
                />
                {errors.name && <span className="rockland-error-message">{errors.name}</span>}
              </div>

              {/* Phone Field */}
              <div className="rockland-form-group">
                <div className="rockland-input-label">
                  <FaPhone className="rockland-input-icon" />
                  <label>Phone</label>
                </div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your Phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`rockland-form-input ${errors.phone ? 'rockland-input-error' : ''}`}
                />
                {errors.phone && <span className="rockland-error-message">{errors.phone}</span>}
              </div>

              {/* Email Field */}
              <div className="rockland-form-group">
                <div className="rockland-input-label">
                  <FaEnvelope className="rockland-input-icon" />
                  <label>Email</label>
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`rockland-form-input ${errors.email ? 'rockland-input-error' : ''}`}
                />
                {errors.email && <span className="rockland-error-message">{errors.email}</span>}
              </div>

              {/* Service Selection */}
              <div className="rockland-form-group">
                <div className="rockland-input-label">
                  <FaStethoscope className="rockland-input-icon" />
                  <label>Select Service</label>
                </div>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className={`rockland-form-select ${errors.service ? 'rockland-input-error' : ''}`}
                >
                  <option value="">Choose a medical service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
                {errors.service && <span className="rockland-error-message">{errors.service}</span>}
              </div>

              {/* Doctor Selection */}
              <div className="rockland-form-group">
                <div className="rockland-input-label">
                  <FaCalendarAlt className="rockland-input-icon" />
                  <label>Doctor Availability</label>
                </div>
                
                <div className="rockland-doctor-options">
                  {doctors.map((doc, index) => (
                    <div 
                      key={index}
                      className={`rockland-doctor-option ${formData.doctor === doc.name ? 'rockland-doctor-selected' : ''} ${!doc.available ? 'rockland-doctor-unavailable' : ''}`}
                      onClick={() => doc.available && handleDoctorSelect(doc.name)}
                    >
                      <div className="rockland-doctor-info">
                        <div className="rockland-doctor-name">
                          {doc.name}
                          <span className="rockland-doctor-specialization"> - {doc.specialization}</span>
                        </div>
                        <div className="rockland-doctor-availability">
                          <FaClock className="rockland-clock-icon" />
                          {doc.availability}
                        </div>
                      </div>
                      <div className="rockland-availability-status">
                        {doc.available ? (
                          <>
                            <FaCheckCircle className="rockland-available-icon" />
                            <span>Available</span>
                          </>
                        ) : (
                          <>
                            <FaTimesCircle className="rockland-unavailable-icon" />
                            <span>Unavailable</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {errors.doctor && <span className="rockland-error-message">{errors.doctor}</span>}
                <input
                  type="hidden"
                  name="doctor"
                  value={formData.doctor}
                  onChange={handleInputChange}
                />
              </div>

              {/* Query Field */}
              <div className="rockland-form-group">
                <div className="rockland-input-label">
                  <FaEnvelope className="rockland-input-icon" />
                  <label>Enter Your Query</label>
                </div>
                <textarea
                  name="query"
                  placeholder="Describe your symptoms, concerns, or questions..."
                  value={formData.query}
                  onChange={handleInputChange}
                  rows="4"
                  className={`rockland-form-textarea ${errors.query ? 'rockland-input-error' : ''}`}
                />
                {errors.query && <span className="rockland-error-message">{errors.query}</span>}
              </div>

              {/* Submit Button */}
              <button type="submit" className="rockland-submit-appointment-btn">
                Book Appointment
              </button>
            </form>
          </div>

          {/* Right Side - Contact Info */}
          <div className="rockland-appointment-info-wrapper">
            <div className="rockland-appointment-info-card">
              <div className="rockland-info-card-header">
                <h3 className="rockland-info-card-title">24/7 Emergency Contact</h3>
                <p className="rockland-info-card-subtitle">
                  Doctors appointments or any other services call us at following number
                </p>
              </div>
              
              <div className="rockland-contact-number">
                <FaPhone className="rockland-contact-phone-icon" />
                <div className="rockland-number-details">
                  <span className="rockland-call-text">Call us at</span>
                  <a href="tel:+918090311155" className="rockland-emergency-number">+91 80903 11155</a>
                </div>
              </div>
              
              <div className="rockland-info-features">
                <div className="rockland-feature-item">
                  <div className="rockland-feature-icon">
                    <FaClock />
                  </div>
                  <div className="rockland-feature-content">
                    <h4>24/7 Availability</h4>
                    <p>Round-the-clock emergency services</p>
                  </div>
                </div>
                
                <div className="rockland-feature-item">
                  <div className="rockland-feature-icon">
                    <FaCalendarAlt />
                  </div>
                  <div className="rockland-feature-content">
                    <h4>Same Day Appointments</h4>
                    <p>Quick scheduling for urgent cases</p>
                  </div>
                </div>
                
                <div className="rockland-feature-item">
                  <div className="rockland-feature-icon">
                    <FaStethoscope />
                  </div>
                  <div className="rockland-feature-content">
                    <h4>Expert Doctors</h4>
                    <p>Qualified and experienced specialists</p>
                  </div>
                </div>
              </div>
              
              <div className="rockland-info-note">
                <p>
                  <strong>Note:</strong> For emergency cases, please call directly. 
                  Online appointments are confirmed within 2 hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {isSubmitted && (
          <div className="rockland-appointment-success">
            <div className="rockland-success-content">
              <FaCheckCircle className="rockland-success-icon" />
              <div className="rockland-success-text">
                <h3>Appointment Booked Successfully!</h3>
                <p>
                  Your appointment has been scheduled. We'll contact you shortly at 
                  <strong> {formData.phone}</strong> for confirmation.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentSection;